"use strict";
ui.loadTemplateFile("vending.html");

const VENDING_SLOT_COUNT = 30;

class VendingDialog extends Dialog {
    _element: any;
    _titleInput: any;
    _sellSlotsContainer: any;
    _buySlotsContainer: any;
    _sellSlots: any[];
    _buySlots: any[];
    _startButton: any;
    _cancelButton: any;
    _tabs: any;
    _data: any;
    _browsingSellEntries: any[];
    _browsingBuyEntries: any[];
    _lastMode: string;

    constructor(vending: any) {
        super("vending.html");

        this._titleInput = ui.textbox(this, ".textbox.title");
        this._tabs = ui.tabpanel(this, ".tabpanel.mode");

        this._sellSlotsContainer = ui.panel(this, ".slots.sell");
        this._buySlotsContainer = ui.panel(this, ".slots.buy");
        this._sellSlots = [];
        this._buySlots = [];

        for (let i = 0; i < VENDING_SLOT_COUNT; ++i) {
            const sellSlot = ui.iconslot(".vending-sell-slot-" + i);
            sellSlot.acceptsItem(true);
            // Assigned once — IconSlot._onDoubleClick calls this.use()
            // unconditionally (gated only by _usable, which defaults true
            // and nothing here ever changes), so a stable per-index handler
            // that branches on the current mode avoids having to reassign
            // .use every render (these 30 slot widgets are reused across
            // setup <-> visitor mode, not recreated).
            sellSlot.use = () => this._onSellSlotUse(i);
            // Drag-out-to-remove: IconSlot emits "swap" on drop regardless
            // of target (canvas, another slot, or nothing matched) — a
            // listed item has nowhere meaningful to swap *to*, so any drag
            // out of the slot just un-lists it, same as double-click.
            sellSlot.on("swap", () => this._removeSellItem(i));
            this._sellSlotsContainer.append(sellSlot);
            this._sellSlots.push(sellSlot);

            const buySlot = ui.iconslot(".vending-buy-slot-" + i);
            buySlot.acceptsItem(true);
            buySlot.use = () => this._onBuySlotUse(i);
            buySlot.on("swap", () => this._removeBuyItem(i));
            this._buySlotsContainer.append(buySlot);
            this._buySlots.push(buySlot);
        }

        this._startButton = ui.button(this, ".button.start");
        this._startButton.on("clicked", this._onStart.bind(this));

        this._cancelButton = ui.button(this, ".button.cancel");
        this._cancelButton.on("clicked", () => {
            if (this._data.browsing) {
                this._data.clearBrowsing();
                this.hide();
            } else {
                this.hide();
            }
        });

        this._data = vending;
        this._data.on("changed", this._update.bind(this));
        this._update();
    }

    _removeSellItem(slotIndex: number) {
        const slotNum = Array.from(this._data.mySellItems.keys())[slotIndex];
        if (slotNum !== undefined) {
            this._data.removeMySellItem(slotNum);
            if (this._data.mode === 'open') {
                netGame.vendingSetSellItem(slotNum, 0, 0);
            }
        }
    }

    _removeBuyItem(slotIndex: number) {
        const slotNum = Array.from(this._data.myBuyItems.keys())[slotIndex];
        if (slotNum !== undefined) {
            this._data.removeMyBuyItem(slotNum);
            if (this._data.mode === 'open') {
                netGame.vendingSetBuyItem(slotNum, 0, 0);
            }
        }
    }

    // Double-click handler for a Selling-tab slot.
    _onSellSlotUse(slotIndex: number) {
        if (this._data.browsing) {
            const entry = this._browsingSellEntries[slotIndex];
            if (entry) {
                this._buyFromSlot(entry);
            }
            return;
        }
        this._removeSellItem(slotIndex);
    }

    // Double-click handler for a Buying-tab slot.
    _onBuySlotUse(slotIndex: number) {
        if (this._data.browsing) {
            const entry = this._browsingBuyEntries[slotIndex];
            if (entry) {
                this._sellToSlot(entry);
            }
            return;
        }
        this._removeBuyItem(slotIndex);
    }

    // Called by InventoryDialog's drag/drop handler when an item is
    // dropped onto a Selling/Buying slot before the shop is open — the
    // quantity+price popup lives here since only setup mode needs it.
    //
    // item.slotNo is the wire slot number pakInventory assigns to an
    // INVENTORY-location item (rawIndex - 12, worldpackets.ts:1390) — both
    // callers here are only reached for ITEMLOC.INVENTORY drops (see
    // inventory.ts's dispatch guard below), so item.slotNo + 12 always
    // recovers the raw owner.items[] index MarketSystem expects, the same
    // conversion trade's existing tradeAddItem(item.slotNo + 12) call makes.
    onItemDroppedOnSell(item: any) {
        if (this._data.browsing) {
            return;
        }
        const isStackable = ITMSTACKABLE[item.itemType] && item.quantity > 1;
        const quantityDialog = ui.quantityDialog(item, {
            min: 1,
            max: isStackable ? item.quantity : 1,
            showPrice: true,
        });
        quantityDialog.on("confirm", (quantity: number, price: number) => {
            const rawSlot = item.slotNo + 12;
            this._data.setMySellItem(rawSlot, item, price, quantity);
            if (this._data.mode === 'open') {
                netGame.vendingSetSellItem(rawSlot, price, quantity);
            }
        });
    }

    onItemDroppedOnBuy(item: any) {
        if (this._data.browsing) {
            return;
        }
        const quantityDialog = ui.quantityDialog(item, {
            min: 1,
            max: 999,
            showPrice: true,
        });
        quantityDialog.on("confirm", (desiredQty: number, price: number) => {
            const rawSlot = item.slotNo + 12;
            this._data.setMyBuyItem(rawSlot, item, price, desiredQty);
            if (this._data.mode === 'open') {
                netGame.vendingSetBuyItem(rawSlot, price, desiredQty);
            }
        });
    }

    _onStart() {
        if (this._data.browsing) {
            return;
        }
        if (this._data.mode === 'open') {
            netGame.vendingClose();
            return;
        }
        const title = String(this._titleInput.text() || '').trim();
        if (!title) {
            GCM.error('Enter a shop name before starting your shop.');
            this._titleInput.focus();
            return;
        }
        const sellItems = Array.from(this._data.mySellItems.entries()).map(([slotNum, e]: any) => ({
            slotNum, price: e.price, quantity: e.quantity,
        }));
        const buyItems = Array.from(this._data.myBuyItems.entries()).map(([slotNum, e]: any) => ({
            slotNum, price: e.price, desiredQty: e.desiredQty,
        }));
        netGame.vendingOpen(sellItems, buyItems, title);
    }

    _update() {
        if (this._data.mode === 'closed' && !this._data.browsing) {
            this.hide();
            return;
        }

        this.show();
        this.center();

        if (this._data.browsing) {
            this._renderBrowsing();
            return;
        }

        this._element.find(".shop-title").text('Vending');
        if (this._data.mode === 'setup' && this._lastMode !== 'setup') {
            this._titleInput.text(this._data.title);
        }
        this._lastMode = this._data.mode;
        this._titleInput._element.prop("disabled", false);
        // The same button opens the shop during setup and stops it while open.
        if (this._data.mode === 'open') {
            this._startButton._element.text('Stop');
            this._startButton.show();
        } else {
            this._startButton._element.text('Start');
            this._startButton.show();
        }

        const sellSlotNums = Array.from(this._data.mySellItems.keys());
        this._sellSlots.forEach((slot, i) => {
            const slotNum = sellSlotNums[i];
            if (slotNum !== undefined) {
                slot.setItem(this._data.mySellItems.get(slotNum).item);
                slot.dragEnabled(true);
            } else {
                slot.clear();
            }
        });

        const buySlotNums = Array.from(this._data.myBuyItems.keys());
        this._buySlots.forEach((slot, i) => {
            const slotNum = buySlotNums[i];
            if (slotNum !== undefined) {
                slot.setItem(this._data.myBuyItems.get(slotNum).item);
                slot.dragEnabled(true);
            } else {
                slot.clear();
            }
        });
    }

    _renderBrowsing() {
        const b = this._data.browsing;
        this._element.find(".shop-title").text(b.title || (b.ownerName + "'s Shop"));
        this._titleInput._element.prop("disabled", true);
        this._startButton.hide();

        // Stashed so _onSellSlotUse/_onBuySlotUse (fired later, on a
        // double-click) can look up which listing a given slot index
        // currently represents — these 30 slot widgets are reused across
        // renders/modes rather than recreated, so their double-click
        // handler (assigned once in the constructor) can't close over a
        // specific entry directly.
        this._browsingSellEntries = Array.from(b.sellItems.values());
        this._sellSlots.forEach((slot, i) => {
            const entry = this._browsingSellEntries[i];
            if (entry) {
                slot.setItem({ itemType: entry.itemtype, itemNo: entry.itemnum, refine: entry.refine, quantity: entry.quantity });
                slot.dragEnabled(false);
            } else {
                slot.clear();
            }
        });

        this._browsingBuyEntries = Array.from(b.buyItems.values());
        this._buySlots.forEach((slot, i) => {
            const entry = this._browsingBuyEntries[i];
            if (entry) {
                slot.setItem({ itemType: entry.itemtype, itemNo: entry.itemnum, refine: entry.refine, quantity: entry.remainingQty });
                slot.dragEnabled(false);
            } else {
                slot.clear();
            }
        });
    }

    _buyFromSlot(entry: any) {
        const ownerClientId = this._data.browsing.ownerClientId;
        if (entry.quantity <= 1) {
            netGame.vendingBuy(ownerClientId, entry.slot, 1);
            return;
        }
        const quantityDialog = ui.quantityDialog(
            { itemType: entry.itemtype, itemNo: entry.itemnum, name: '' },
            { min: 1, max: entry.quantity }
        );
        quantityDialog.on("confirm", (quantity: number) => {
            netGame.vendingBuy(ownerClientId, entry.slot, quantity);
        });
    }

    _sellToSlot(entry: any) {
        const ownerClientId = this._data.browsing.ownerClientId;
        const remaining = entry.remainingQty; // server already sends desiredQty-filledQty, see the 0x7ca parser
        if (remaining <= 1) {
            netGame.vendingSell(ownerClientId, entry.slot, 1);
            return;
        }
        const quantityDialog = ui.quantityDialog(
            { itemType: entry.itemtype, itemNo: entry.itemnum, name: '' },
            { min: 1, max: remaining }
        );
        quantityDialog.on("confirm", (quantity: number) => {
            netGame.vendingSell(ownerClientId, entry.slot, quantity);
        });
    }
}

ui.VendingDialog = VendingDialog;

ui.vendingDialog = (vending: any) => {
    return new ui.VendingDialog(vending);
};
